import React, { useEffect, useState } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import { useParams } from "react-router-dom";
import Link, { LocalizedNavigate as Navigate } from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import SchemaMarkup, { buildArticle, buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api, resolveImage } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const { t, lang } = useI18n();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        if (cancel) return;
        setPost(data);
        const { data: list } = await api.get("/blog");
        if (cancel) return;
        setRelated(list.filter((p) => p.slug !== slug).slice(0, 3));
      } catch (_err) {
        if (!cancel) setNotFound(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  useDocumentMeta({
    title: post ? (post.seo_title || `${post.title} | Finca Cortesin New Developments`) : "",
    description: post ? (post.meta_description || post.excerpt || "") : "",
    image: post?.image || "",
  });

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) return <div className="min-h-screen bg-[#F9F6F0]" />;

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup schema={buildArticle(post)} />
      <SchemaMarkup schema={buildBreadcrumb([{ name: t("home"), url: "/" }, { name: t("blog_breadcrumb"), url: "/blog" }, { name: post.title, url: `/blog/${post.slug}` }])} />

      <section className="pt-32 pb-12">
        <div className="max-w-[920px] mx-auto px-6 md:px-12 lg:px-16">
          <Link to="/blog" data-testid="back-to-blog" className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] hover:text-[#C5A880] mb-8 inline-block">
            {t("blog_all_articles")}
          </Link>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">
            {new Date(post.date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { year: "numeric", month: "long", day: "numeric" })} · {post.readTime}
          </p>
          <h1 data-testid="post-title" className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {post.title}
          </h1>
          <p className="text-lg lg:text-xl text-[#73706B] mt-6 leading-relaxed font-light">{post.excerpt}</p>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="aspect-[16/9] overflow-hidden">
            <ResponsiveImg src={post.image} sizes={SIZES.article} priority alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-[760px] mx-auto px-6 md:px-12 lg:px-16">
          <article
            className="prose prose-stone max-w-none text-lg lg:text-xl leading-relaxed text-[#1A1A1A] font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            dangerouslySetInnerHTML={{ __html: post.body }}
          />
          <div className="mt-12 pt-10 border-t border-[#E5DFD5]">
            <Link to="/contact?request=consultation" data-testid="post-cta" className="inline-block bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors">
              {t("blog_cta")}
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-[#FFFFFF] border-t border-[#E5DFD5]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("blog_continue")}</p>
            <h2 className="text-3xl lg:text-4xl font-light mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("blog_related")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <ResponsiveImg src={p.image} sizes={SIZES.third} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                  </div>
                  <h3 className="text-2xl font-light group-hover:text-[#C5A880]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
